import { useState } from "react";
import { ArrowRight, Briefcase, HeartPulse, Users, Coffee } from "lucide-react";
import { toast } from "sonner";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";
import { sendForm } from "../lib/forms";
import { IMAGES } from "../data/content";

const OFFERS = [
  { icon: Users, title: "Wellness workshops & off-sites", text: "Half- or full-day sessions on stress, listening and team trust — designed for real workplaces, not brochures." },
  { icon: HeartPulse, title: "EAP-lite programme", text: "A light-touch employee assistance layer: confidential one-to-ones for your team, monthly reporting, zero bureaucracy." },
  { icon: Briefcase, title: "Executive one-to-ones", text: "Private, senior-confidential sessions for leaders carrying weight they can't put down on the office floor." },
  { icon: Coffee, title: "Team reset days", text: "A gentle monthly ritual: facilitated check-ins, micro-practices and a team that talks easier by Friday." },
];

export default function CorporateSolutions() {
  const [form, setForm] = useState({ name: "", email: "", organization: "", team_size: "1–20", topic: "Workshops & off-sites", message: "" });
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await sendForm("Corporate inquiry", form);
      setSent(true);
      toast.success("Thank you — we'll be in touch within one working day.");
    } catch {
      toast.error("Couldn't send right now. Please email connectbackofthemind@gmail.com.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <PageHero
        eyebrow="Corporate Solutions"
        lines={["Rooms that breathe.", "Teams that", "talk easier."]}
        sub="Workshops, employee assistance and executive support for workplaces where people are tired of pretending they're fine."
        image={IMAGES.corporateLounge}
        alt="A calm, modern office lounge"
      />

      <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8">
        <Reveal>
          <p className="eyebrow">What we bring to work</p>
          <h2 className="mt-4 font-serif text-4xl tracking-tight text-ink sm:text-5xl">
            Four programmes, built for real offices.
          </h2>
        </Reveal>
        <div className="mt-12 grid gap-5 sm:grid-cols-2">
          {OFFERS.map((o, i) => (
            <Reveal key={o.title} delay={i * 0.08}>
              <div className="card-soft h-full p-8" data-testid={`corporate-offer-${i}`}>
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-sagelight text-pine">
                  <o.icon className="h-5 w-5" />
                </span>
                <h3 className="mt-6 font-serif text-2xl text-ink">{o.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-bodytext">{o.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-pinedeep py-24 text-cream">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-blush">
              Why it pays to be kind at work
            </p>
            <h2 className="mt-4 max-w-3xl font-serif text-4xl leading-tight sm:text-5xl">
              Burnout rarely announces itself.{" "}
              <span className="italic text-blush">It invoices you later.</span>
            </h2>
            <p className="mt-6 max-w-2xl text-cream/75">
              Every programme starts with listening: a quiet assessment of how your teams
              actually are, a workshop plan shaped around it, and a walk-along rhythm that
              outlives the launch email.
            </p>
          </Reveal>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {[
              { n: "Listen", t: "A confidential wellbeing pulse — anonymous, kind, specific." },
              { n: "Shape", t: "Workshops and support built on what was actually said." },
              { n: "Walk along", t: "Monthly rhythm, office hours and a human who knows your name." },
            ].map((s, i) => (
              <Reveal key={s.n} delay={i * 0.1}>
                <div className="rounded-3xl border border-cream/15 p-7" data-testid={`corporate-approach-${i}`}>
                  <p className="font-serif text-2xl italic text-blush">{s.n}</p>
                  <p className="mt-3 text-sm leading-relaxed text-cream/75">{s.t}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8">
        <div className="grid gap-14 lg:grid-cols-[0.9fr_1.1fr]">
          <Reveal>
            <p className="eyebrow">B2B inquiries</p>
            <h2 className="mt-4 font-serif text-4xl leading-tight tracking-tight text-ink sm:text-5xl">
              Tell us about your team. We'll tell you what we'd do.
            </h2>
            <p className="mt-6 max-w-md leading-relaxed text-bodytext">
              Every workplace is a different mountain. Share a few lines and we'll come
              back within one working day with an honest, right-sized proposal — even if
              the honest answer is "you need less than you think."
            </p>
            <p className="mt-8 font-serif text-xl italic text-pine">
              "The view is better when someone is laughing beside you."
            </p>
          </Reveal>

          <Reveal delay={0.12}>
            {sent ? (
              <div data-testid="corporate-form-success" className="card-soft p-10 text-center">
                <h3 className="font-serif text-3xl text-ink">Thank you.</h3>
                <p className="mx-auto mt-4 max-w-sm text-sm leading-relaxed text-bodytext">
                  Your inquiry is with us. Expect a thoughtful reply at{" "}
                  <strong>{form.email}</strong> within one working day.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="card-soft grid gap-4 p-8 sm:grid-cols-2" data-testid="corporate-inquiry-form">
                <input
                  data-testid="corporate-name-input"
                  required
                  className="input-soft"
                  placeholder="Your name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
                <input
                  data-testid="corporate-email-input"
                  required
                  type="email"
                  className="input-soft"
                  placeholder="Work email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
                <input
                  data-testid="corporate-org-input"
                  required
                  className="input-soft"
                  placeholder="Organization"
                  value={form.organization}
                  onChange={(e) => setForm({ ...form, organization: e.target.value })}
                />
                <select
                  data-testid="corporate-size-select"
                  className="input-soft"
                  value={form.team_size}
                  onChange={(e) => setForm({ ...form, team_size: e.target.value })}
                  aria-label="Team size"
                >
                  {["1–20", "21–100", "101–500", "500+"].map((s) => (
                    <option key={s}>{s}</option>
                  ))}
                </select>
                <select
                  data-testid="corporate-interest-select"
                  className="input-soft sm:col-span-2"
                  value={form.topic}
                  onChange={(e) => setForm({ ...form, topic: e.target.value })}
                  aria-label="What are you interested in"
                >
                  {OFFERS.map((o) => (
                    <option key={o.title}>{o.title}</option>
                  ))}
                </select>
                <textarea
                  data-testid="corporate-message-input"
                  required
                  className="input-soft min-h-32 sm:col-span-2"
                  placeholder="A few lines about your team and what you're noticing…"
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                />
                <button
                  data-testid="corporate-submit-button"
                  disabled={sending}
                  className="flex items-center justify-center gap-2 rounded-full bg-pine py-3.5 text-sm font-semibold text-cream transition-all hover:-translate-y-0.5 hover:bg-pinedeep disabled:opacity-60 sm:col-span-2"
                >
                  {sending ? "Sending…" : "Send inquiry"}
                  {!sending && <ArrowRight className="h-4 w-4" />}
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </div>
  );
}
